import { formatEther } from 'viem';

/**
 * Format token amounts from bigint to human-readable string
 * @param amount - Token amount in wei
 * @returns Formatted string with locale and proper decimals
 */
export const formatTokenAmount = (amount: bigint | undefined): string => {
  if (!amount) return '0.00';
  const formatted = formatEther(amount);
  const num = parseFloat(formatted);
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
};

/**
 * Format error messages to be user-friendly
 * @param error - Error object from transaction
 * @returns User-friendly error message
 */
export const formatErrorMessage = (error: Error | null | undefined): string => {
  if (!error) return 'An error occurred. Please try again.';

  const message = error.message;

  // Check for user rejection
  if (
    message.includes('User rejected') ||
    message.includes('User denied') ||
    message.includes('User cancelled') ||
    message.includes('user rejected')
  ) {
    return 'Transaction was cancelled. Click the button again when you\'re ready to proceed.';
  }

  // Check for insufficient funds
  if (message.includes('insufficient funds')) {
    return 'Insufficient funds to complete this transaction. Please check your ETH balance for gas fees.';
  }

  // Check for network errors
  if (message.includes('network')) {
    return 'Network error occurred. Please check your connection and try again.';
  }

  // For other errors, show a cleaner version
  return 'Transaction failed. Please try again or contact support if the issue persists.';
};

/**
 * Get blockers preventing swap execution
 * @returns Array of blocker messages
 */
export const getSwapBlockers = (
  swapActive: boolean | undefined,
  swapAmount: string,
  swapAmountBigInt: bigint,
  oldTokenBalance: bigint | undefined,
  availablePoolTokens: bigint,
  isApproved: boolean
): string[] => {
  const blockers: string[] = [];

  if (!swapActive) {
    blockers.push('Swapping is currently disabled by the contract administrator');
  }

  if (!swapAmount || swapAmountBigInt === 0n) {
    blockers.push('Please enter an amount to swap');
  }

  if (oldTokenBalance !== undefined && swapAmountBigInt > oldTokenBalance) {
    blockers.push(`Insufficient cFAR balance. You have ${formatTokenAmount(oldTokenBalance)} cFAR`);
  }

  if (availablePoolTokens < swapAmountBigInt) {
    blockers.push(`Insufficient nFAR in contract pool. Only ${formatTokenAmount(availablePoolTokens)} nFAR available`);
  }

  if (!isApproved && swapAmountBigInt > 0n) {
    blockers.push('You need to approve the contract first');
  }

  return blockers;
};
